import React, { useState, useEffect } from 'react';

const getTimeLeft = (targetHour, targetMinute) => {
  const now = new Date();
  
  // Create target time for today
  const targetTime = new Date();
  targetTime.setHours(targetHour, targetMinute, 0, 0);
  
  // If target time has passed today, set it for tomorrow
  if (targetTime <= now) {
    targetTime.setDate(targetTime.getDate() + 1);
  }
  
  const difference = targetTime - now;
  
  // If difference is negative, return zeros
  if (difference < 0) {
    return { hours: 0, minutes: 0, seconds: 0, difference: 0 };
  }

  const hours = Math.floor(difference / (1000 * 60 * 60));
  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((difference % (1000 * 60)) / 1000);

  return { hours, minutes, seconds, difference };
};

const ProfessionalCountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0, difference: 0 });
  const [phase, setPhase] = useState('pre-market');

  useEffect(() => {
    const updateTimer = () => {
      const now = new Date();
      
      // Convert to EST/EDT
      const estTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
      const currentHour = estTime.getHours();
      const currentMinute = estTime.getMinutes();
      
      let targetHour = 9;
      let targetMinute = 30; // Market opens at 9:30 AM EST
      let currentPhase = 'pre-market';

      // Check if we're in trading hours (9:30 AM - 4:00 PM EST)
      if (currentHour > 9 || (currentHour === 9 && currentMinute >= 30)) {
        if (currentHour < 16) {
          // During trading hours - count down to close
          targetHour = 16;
          targetMinute = 0;
          currentPhase = 'trading';
        } else {
          // After hours - count down to next open
          targetHour = 9;
          targetMinute = 30;
          currentPhase = 'after-hours';
        }
      }

      const newTimeLeft = getTimeLeft(targetHour, targetMinute);
      
      // Check if we're within 30 minutes of close
      if (currentPhase === 'trading' && newTimeLeft.difference < 30 * 60 * 1000 && newTimeLeft.difference > 0) {
        currentPhase = 'closing-soon';
      }

      setTimeLeft(newTimeLeft);
      setPhase(currentPhase);
    };

    updateTimer();
    const timer = setInterval(updateTimer, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (time) => String(time).padStart(2, '0');

  const getPhaseConfig = () => {
    switch (phase) {
      case 'trading':
        return {
          label: 'MARKET CLOSES IN',
          color: '#00b746',
          bgColor: 'rgba(0, 183, 70, 0.1)',
          borderColor: 'rgba(0, 183, 70, 0.3)'
        };
      case 'closing-soon':
        return {
          label: 'CLOSING SOON',
          color: '#ff9500',
          bgColor: 'rgba(255, 149, 0, 0.1)',
          borderColor: 'rgba(255, 149, 0, 0.3)'
        };
      case 'after-hours':
        return {
          label: 'MARKET CLOSED',
          color: '#8e8e93',
          bgColor: 'rgba(142, 142, 147, 0.1)',
          borderColor: 'rgba(142, 142, 147, 0.3)'
        };
      default:
        return {
          label: 'MARKET OPENS IN',
          color: '#007aff',
          bgColor: 'rgba(0, 122, 255, 0.1)',
          borderColor: 'rgba(0, 122, 255, 0.3)'
        };
    }
  };

  const config = getPhaseConfig();

  return (
    <div style={{
      display: 'inline-flex',
      flexDirection: 'column',
      alignItems: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      padding: '8px 16px',
      backgroundColor: config.bgColor,
      border: `1px solid ${config.borderColor}`,
      borderRadius: '6px',
      minWidth: '200px'
    }}>
      <div style={{
        fontSize: '10px',
        fontWeight: '600',
        letterSpacing: '0.5px',
        color: config.color,
        marginBottom: '4px',
        textTransform: 'uppercase'
      }}>
        {config.label}
      </div>
      
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontVariantNumeric: 'tabular-nums',
        fontFeatureSettings: '"tnum"'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <span style={{
            fontSize: '24px',
            fontWeight: '600',
            color: config.color,
            lineHeight: '1',
            minWidth: '32px',
            textAlign: 'center'
          }}>
            {formatTime(timeLeft.hours)}
          </span>
          <span style={{
            fontSize: '9px',
            color: '#8e8e93',
            marginTop: '2px',
            textTransform: 'uppercase',
            letterSpacing: '0.3px'
          }}>
            hrs
          </span>
        </div>
        
        <span style={{
          fontSize: '20px',
          fontWeight: '300',
          color: config.color,
          opacity: 0.5,
          margin: '0 2px'
        }}>
          :
        </span>
        
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <span style={{
            fontSize: '24px',
            fontWeight: '600',
            color: config.color,
            lineHeight: '1',
            minWidth: '32px',
            textAlign: 'center'
          }}>
            {formatTime(timeLeft.minutes)}
          </span>
          <span style={{
            fontSize: '9px',
            color: '#8e8e93',
            marginTop: '2px',
            textTransform: 'uppercase',
            letterSpacing: '0.3px'
          }}>
            min
          </span>
        </div>
        
        <span style={{
          fontSize: '20px',
          fontWeight: '300',
          color: config.color,
          opacity: 0.5,
          margin: '0 2px'
        }}>
          :
        </span>
        
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <span style={{
            fontSize: '24px',
            fontWeight: '600',
            color: config.color,
            lineHeight: '1',
            minWidth: '32px',
            textAlign: 'center'
          }}>
            {formatTime(timeLeft.seconds)}
          </span>
          <span style={{
            fontSize: '9px',
            color: '#8e8e93',
            marginTop: '2px',
            textTransform: 'uppercase',
            letterSpacing: '0.3px'
          }}>
            sec
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalCountdownTimer;